'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useI18n } from '../../i18n/LocaleProvider.js';
import { useFocusTrap } from '../../lib/useFocusTrap.js';
import { cn } from '../../lib/cn.js';
import styles from './Modal.module.css';
/**
 * Focus-trapped dialog (WCAG AA, CLAUDE_DESIGN.md §9): moves focus in on
 * open, traps Tab/Shift+Tab, Esc + backdrop dismiss (non-destructive),
 * restores focus to the trigger on close, `aria-modal` + labelled by title.
 * Modal owns the "inert sibling content" guarantee itself (ux.md Modals):
 * while open, every `document.body` child other than the portal node gets
 * `inert` + `aria-hidden="true"` (both set directly as attributes so it
 * works even in environments — jsdom included — that only implement the
 * reflected attribute, not the `inert` IDL property), restored on close.
 */
export function Modal({ open, onClose, title, description, children, footer, state = 'open', scrollableBody = false, size = 'md', className, }) {
    const { t } = useI18n();
    const containerRef = useFocusTrap(open);
    const overlayRef = useRef(null);
    const titleId = useId();
    const descriptionId = useId();
    useEffect(() => {
        if (!open)
            return;
        function onKeyDown(event) {
            if (event.key === 'Escape')
                onClose();
        }
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, onClose]);
    useEffect(() => {
        if (!open || typeof document === 'undefined')
            return;
        const portalNode = overlayRef.current;
        if (!portalNode)
            return;
        const siblings = Array.from(document.body.children).filter((node) => node instanceof HTMLElement && node !== portalNode);
        const restoreFns = siblings.map((el) => {
            const previousInert = el.getAttribute('inert');
            const previousAriaHidden = el.getAttribute('aria-hidden');
            el.setAttribute('inert', '');
            el.setAttribute('aria-hidden', 'true');
            return () => {
                if (previousInert === null)
                    el.removeAttribute('inert');
                else
                    el.setAttribute('inert', previousInert);
                if (previousAriaHidden === null)
                    el.removeAttribute('aria-hidden');
                else
                    el.setAttribute('aria-hidden', previousAriaHidden);
            };
        });
        return () => {
            for (const restore of restoreFns)
                restore();
        };
    }, [open]);
    if (!open || typeof document === 'undefined')
        return null;
    return createPortal(_jsx("div", { ref: overlayRef, className: styles['overlay'], onMouseDown: (event) => event.target === event.currentTarget && onClose(), children: _jsxs("div", { ref: containerRef, role: "dialog", "aria-modal": "true", "aria-labelledby": titleId, "aria-describedby": description ? descriptionId : undefined, 
            // Fallback focus target (`useFocusTrap`) when the dialog body hasn't
            // rendered a focusable control yet (e.g. `loading` state) — never
            // part of the normal Tab order otherwise.
            tabIndex: -1, className: cn(styles['modal'], styles[size], className), children: [_jsxs("div", { className: styles['header'], children: [_jsx("h2", { id: titleId, className: styles['title'], children: title }), _jsx("button", { type: "button", className: styles['closeButton'], onClick: onClose, "aria-label": t('common:action.close'), children: _jsx(X, { size: 18, "aria-hidden": "true" }) })] }), description && (_jsx("p", { id: descriptionId, className: styles['description'], children: description })), _jsxs("div", { className: cn(styles['body'], scrollableBody && styles['scrollableBody']), children: [state === 'loading' && (_jsx("div", { className: styles['stateFill'], "aria-busy": "true", children: t('common:state.loading') })), state === 'error' && (_jsx("div", { role: "alert", className: styles['stateFill'], children: t('datatable:error.title') })), state === 'empty' && _jsx("div", { className: styles['stateFill'], children: t('datatable:empty.firstUse.title') }), state === 'open' && children] }), footer && _jsx("div", { className: styles['footer'], children: footer })] }) }), document.body);
}
